import client from "../client/client";
import { supabase } from "../lib/supabase";
import { TOKEN, ROL, USER, USERNAME, PERMISSIONS, COMPANY_ID, COMPANY_NAME } from "../utils/constants";

// Mapea los permisos que devuelve el backend ([{tab, control, ...}]) al shape
// que usa el front para el gating ([{tab, control, permission}]).
const mapPermissions = (perms) =>
  (perms || [])
    .filter((p) => p && p.tab && p.control)
    .map((p) => ({
      tab: p.tab,
      control: p.control,
      permission: `${p.tab}_${p.control}`,
    }));

// Devuelve la membership activa (la de active_company_id) del modelo nuevo, o null.
const activeMembership = (user) => {
  const memberships = user?.memberships;
  if (!Array.isArray(memberships) || memberships.length === 0) return null;
  const activeId = user?.active_company_id;
  return memberships.find((m) => m.company_id === activeId) || memberships[0];
};

// Resuelve el rol priorizando la membership activa (modelo de membresías) y
// cayendo a is_owner/is_seller del modelo viejo. Así el front queda listo para
// el cutover (cuando se dropeen esos flags) sin cambiar el comportamiento actual.
const resolveRole = (user) => {
  const m = activeMembership(user);
  if (m?.role) return m.role;
  if (user?.is_owner) return "owner";
  if (user?.is_seller) return "seller";
  return "operator";
};

// Completa la sesión tras autenticación (Supabase + backend).
const completeLogin = async (email) => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session.access_token;
  sessionStorage.setItem(TOKEN, token);

  const meResp = await client.get("/user-api/auth", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const user = meResp.data;

  sessionStorage.setItem(USER, String(user?.id ?? ""));
  sessionStorage.setItem(USERNAME, user?.email || email);
  sessionStorage.setItem(ROL, resolveRole(user));
  sessionStorage.setItem(PERMISSIONS, JSON.stringify(mapPermissions(user?.permissions)));

  const m = activeMembership(user);
  const companyId = m?.company_id ?? user?.company?.id ?? "";
  const companyName = m?.company?.name ?? user?.company?.name ?? "";
  sessionStorage.setItem(COMPANY_ID, String(companyId));
  sessionStorage.setItem(COMPANY_NAME, companyName || "");

  return { status: 200, data: { token } };
};

const MFA_VERIFIED_AT_KEY = "voras:mfa_verified_at";
const MFA_TTL_MS = 12 * 60 * 60 * 1000; // 12 horas

const isMfaStillValid = () => {
  const ts = localStorage.getItem(MFA_VERIFIED_AT_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < MFA_TTL_MS;
};

const stampMfaVerified = () => {
  localStorage.setItem(MFA_VERIFIED_AT_KEY, String(Date.now()));
};

// Login contra Supabase Auth. MFA es obligatorio:
// - Sin factor enrollado → lanza "mfa_setup_required" para que login.js
//   abra el modal de setup (QR + verificación).
// - Con factor enrollado → si la última verificación fue hace menos de 12hs,
//   entra directo; si no, lanza "mfa_required" para pedir el código.
const singIn = async (data) => {
  const email = data.name;
  const { data: sess, error } = await supabase.auth.signInWithPassword({
    email,
    password: data.password,
  });

  if (error) {
    const e = new Error(error.message);
    e.response = { status: 401, data: { error: "invalid_credentials" } };
    throw e;
  }

  const { data: factors } = await supabase.auth.mfa.listFactors();
  const totpFactor = factors?.totp?.find((f) => f.status === "verified");

  if (!totpFactor) {
    const e = new Error("MFA setup required");
    e.response = {
      status: 401,
      data: { error: "mfa_setup_required" },
    };
    throw e;
  }

  if (isMfaStillValid()) {
    return completeLogin(email);
  }

  const e = new Error("MFA required");
  e.response = {
    status: 401,
    data: { error: "mfa_required", factorId: totpFactor.id },
  };
  throw e;
};

// Verifica el código TOTP contra Supabase MFA y completa la sesión.
// Guarda el timestamp para el TTL de 12hs.
const verifyMfaCode = async (factorId, code) => {
  const { data: challenge, error: challengeErr } =
    await supabase.auth.mfa.challenge({ factorId });
  if (challengeErr) throw challengeErr;

  const { error: verifyErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyErr) throw verifyErr;

  stampMfaVerified();
  return completeLogin("");
};

// Enrolla un nuevo factor TOTP en Supabase. Devuelve { id, totp: { uri, secret } }.
const enrollMfa = async () => {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName: "Voras Authenticator",
  });
  if (error) throw error;
  return data;
};

// Desvincula un factor MFA.
const unenrollMfa = async (factorId) => {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
};

const singOut = () => {
  supabase.auth.signOut().catch(() => {});
  sessionStorage.clear();
  localStorage.removeItem(MFA_VERIFIED_AT_KEY);
};

/** Solicitud de recuperación de contraseña (correo con instrucciones, vía Supabase). */
const requestPasswordReset = async (data) => {
  const email = data?.email || data?.name;
  // El link del mail debe volver a la pantalla que setea la pass nueva, NO al
  // formulario que pide el email. Supabase agrega el token de recovery al hash.
  const redirectTo = `${window.location.origin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  // supabase-js NO tira excepción: devuelve { error }. Lo propagamos para que el
  // form pueda distinguir un fallo real (rate-limit, redirect no permitido) del
  // caso enumeration-safe de email inexistente (que Supabase reporta como OK).
  if (error) {
    const e = new Error(error.message);
    e.response = { status: error.status || 400, data: { message: error.message } };
    throw e;
  }
};

/**
 * Auto-registro del owner: crea el usuario en Supabase Auth con la contraseña
 * elegida y dispara el mail "Confirm sign up". (El alta de colaboradores NO usa
 * esto: va por el invite del backend.)
 */
const signUp = async ({ email, password }) => {
  const emailRedirectTo = `${window.location.origin}/login`;
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo },
  });
  if (error) {
    const e = new Error(error.message);
    e.response = { status: error.status || 400, data: { message: error.message } };
    throw e;
  }
  // Supabase, para no filtrar qué emails existen, devuelve un user "fantasma"
  // (sin identities) cuando el email ya está registrado, en vez de error.
  if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    const e = new Error("email_already_registered");
    e.response = { status: 409, data: { message: "Ya existe una cuenta con ese email." } };
    throw e;
  }
  return data;
};

/** Setea la nueva contraseña usando la sesión de recovery activa (post-link). */
const updatePassword = async (newPassword) => {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    const e = new Error(error.message);
    e.response = { status: error.status || 400, data: { message: error.message } };
    throw e;
  }
};

const getUserPermissions = () => {
  const permissions = sessionStorage.getItem(PERMISSIONS);
  if (!permissions || permissions === "undefined" || permissions === "null") {
    return [];
  }
  
  try {
    return JSON.parse(permissions);
  } catch (error) {
    console.error("Error parsing permissions from sessionStorage:", error);
    return [];
  }
};

const hasPermission = (controlName, tabName = null) => {
  // Mismo bypass de owner que canAccessTab: el owner es la raíz de su tenant y
  // tiene acceso total, aunque no tenga filas en users_permissions (el alta de
  // owner no asigna permisos granulares).
  if (isOwner()) return true;
  const permissions = getUserPermissions();
  return permissions.some(
    (p) => p.control === controlName && (tabName === null || p.tab === tabName)
  );
};

const hasTabPermission = (tab) => {
  const permissions = getUserPermissions();
  return permissions.some((permission) => permission.tab === tab);
};

// El owner es la raíz de su tenant y tiene acceso total. Se usa para el bypass
// de permisos tanto en los guards de ruta (canAccessTab) como en el menú.
const isOwner = () => sessionStorage.getItem(ROL) === "owner";

// Gating de acceso a una sección (tab). El owner siempre tiene acceso total
// (es la raíz de su tenant); el resto (vendedor/operador) según sus permisos.
// Se usa tanto para ocultar items de menú como para los guards de ruta.
const canAccessTab = (tab) => {
  if (isOwner()) return true;
  return hasTabPermission(tab);
};

let AuthService = {
  singIn,
  singOut,
  signUp,
  requestPasswordReset,
  updatePassword,
  verifyMfaCode,
  enrollMfa,
  unenrollMfa,
  hasPermission,
  getUserPermissions,
  hasTabPermission,
  canAccessTab,
  isOwner,
};

export default AuthService;